// Sync queue for offline-first PWA writes.
// Wraps IndexedDB helpers in offlineStorage.js and adds:
//   • automatic flush on `online` event
//   • visible queued-count via useSyncQueue() hook
//   • retry-with-backoff per action (last_error captured)
//
// Each action: { id, method, url, headers, body, createdAt, attempts, lastError }
import { useEffect, useState, useCallback } from 'react';
import {
  initDB, saveOfflineAction, getOfflineActions, removeOfflineAction
} from './offlineStorage';
import { readSharedToken } from './authTokenKeys';
import { authHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_ATTEMPTS = 5;

let _flushInFlight = false;
const _listeners = new Set();
function _notify() { _listeners.forEach(fn => { try { fn(); } catch { /* swallow */ } }); }

// Was: localStorage.getItem('token') || localStorage.getItem('mobileToken')
// — never checked `auth_token`, the ONLY key a native mobile login writes
// (MobileAuthProvider.login()). Any action queued after a native login had
// no Authorization header, got 401'd on flush, and was silently dropped as
// an unfixable 4xx (audit-2026-09-07 §2). Now shares one implementation
// with MobileAuthProvider so the two can't drift apart again.
const getToken = () => readSharedToken();

/** Submit an authed write. If offline OR network fails, queue it. */
export async function queueOrSend({ method = 'POST', path, body, headers = {}, requiresScope = false, queueType }) {
  const url = `${API_URL}${path}`;
  const token = getToken();
  const scopeHeaders = authHeaders({ token, locationId: localStorage.getItem('current_location_id') || '' });
  if (requiresScope && (!scopeHeaders.Authorization || !scopeHeaders['X-Organization-Id'] || !scopeHeaders['X-Location-Id'])) {
    return { ok: false, status: 0, error: 'Sign in and choose an organization and site before recording safety data.' };
  }
  const merged = {
    'Content-Type': 'application/json',
    ...headers,
    // Callers may add domain headers, but cannot replace the active session
    // or tenant/site context after it has passed the scope check above.
    ...scopeHeaders,
  };

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return enqueue({ method, url, headers: merged, body, queueType });
  }

  try {
    const res = await fetch(url, {
      method,
      headers: merged,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      // 5xx → queue; 4xx → surface error (queueing won't help)
      if (res.status >= 500) return enqueue({ method, url, headers: merged, body, queueType });
      const detail = await res.json().catch(() => ({}));
      return { ok: false, status: res.status, error: detail.detail || res.statusText };
    }
    const json = await res.json().catch(() => ({}));
    return { ok: true, status: res.status, data: json };
  } catch {
    // network down — queue it
    return enqueue({ method, url, headers: merged, body, queueType });
  }
}

async function enqueue(action) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const stored = {
    id,
    method: action.method,
    url: action.url,
    headers: action.headers,
    body: action.body,
    ...(action.queueType ? { queueType: action.queueType } : {}),
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
  };
  await initDB();
  await saveOfflineAction(stored);
  _notify();
  return { ok: false, queued: true, id, status: 0 };
}

/** Flush queued actions. Called on `online` event and on demand. */
export async function flushQueue() {
  if (_flushInFlight) return { processed: 0, succeeded: 0 };
  _flushInFlight = true;
  let processed = 0, succeeded = 0;
  try {
    const actions = await getOfflineActions();
    for (const act of actions) {
      processed += 1;
      const isSafetyAction = act.queueType === 'safety' || act.url?.includes('/api/mobile/safety/');
      if (isSafetyAction) {
        const currentHeaders = authHeaders({ token: getToken(), locationId: localStorage.getItem('current_location_id') || '' });
        const capturedHeaders = act.headers || {};
        const sameScope = currentHeaders.Authorization
          && currentHeaders.Authorization === capturedHeaders.Authorization
          && currentHeaders['X-Organization-Id']
          && currentHeaders['X-Organization-Id'] === capturedHeaders['X-Organization-Id']
          && currentHeaders['X-Location-Id']
          && currentHeaders['X-Location-Id'] === capturedHeaders['X-Location-Id'];
        if (!sameScope) {
          act.lastError = 'Safety record is waiting for the original signed-in account and site.';
          await saveOfflineAction(act).catch(() => {});
          continue;
        }
      }
      try {
        const res = await fetch(act.url, {
          method: act.method,
          headers: act.headers,
          body: act.body ? JSON.stringify(act.body) : undefined,
        });
        const isAuthFailure = res.status === 401 || res.status === 403;
        if (res.ok || (res.status >= 400 && res.status < 500 && !isAuthFailure)) {
          // 2xx = success; 4xx (other than 401/403) = genuinely-invalid
          // request that a retry cannot fix (bad payload, not-found, etc.) —
          // drop it.
          await removeOfflineAction(act.id);
          if (res.ok) succeeded += 1;
        } else {
          // 5xx / network / 401 / 403 — leave queued, bump attempts. A 401/403
          // IS fixable by retry once re-authentication happens; treating it
          // the same as an unfixable 4xx silently destroyed real operator
          // work (a stock count, a booking) with no error shown and no way
          // to recover it (audit-2026-09-07 §2).
          act.attempts = (act.attempts || 0) + 1;
          act.lastError = `HTTP ${res.status}`;
          if (act.attempts >= MAX_ATTEMPTS) {
            await removeOfflineAction(act.id); // give up after MAX_ATTEMPTS
          } else {
            await saveOfflineAction(act).catch(() => {});
          }
        }
      } catch (e) {
        act.attempts = (act.attempts || 0) + 1;
        act.lastError = String(e);
        if (act.attempts >= MAX_ATTEMPTS) {
          await removeOfflineAction(act.id);
        } else {
          await saveOfflineAction(act).catch(() => {});
        }
      }
    }
  } finally {
    _flushInFlight = false;
    _notify();
  }
  return { processed, succeeded };
}

/** React hook: live count of queued actions + online flag. */
export function useSyncQueue() {
  const [count, setCount] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  const refresh = useCallback(async () => {
    try {
      const items = await getOfflineActions();
      setCount(Array.isArray(items) ? items.length : 0);
    } catch {
      setCount(0);
    }
  }, []);

  useEffect(() => {
    refresh();
    _listeners.add(refresh);
    const onOnline  = () => { setOnline(true);  flushQueue().then(refresh); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    // Best-effort flush on mount in case we came back online before listener bind
    if (navigator.onLine) flushQueue().then(refresh);
    return () => {
      _listeners.delete(refresh);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [refresh]);

  return { queuedCount: count, online, flush: () => flushQueue().then(refresh) };
}
